import React from "react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { X } from "lucide-react";

/**
 * Pre-save re-analysis prompt.
 *
 * IMPORTANT: the profile edit has NOT been saved yet when this opens. The save
 * is held until the user chooses, and there are three genuinely distinct exits:
 *
 *   • "Retrigger and Save" (onConfirm) — save, then queue the re-analysis.
 *   • "Save as it is"      (onSkip)    — save only.
 *   • "Cancel" / "X"       (onDismiss) — save NOTHING, stay on the edit page.
 *
 * Do not collapse Cancel/X into onSkip: dismissing a confirmation must not be
 * a way to accidentally commit the very thing it was confirming, and it must
 * not navigate away from the user's unsaved edits.
 *
 * Shown only when the server's dry run reported that score-affecting fields
 * actually changed, so it never nags on a cosmetic edit (a phone-number fix
 * returns an empty list and saves straight through without this modal).
 *
 * Re-analyses only the application being edited, never the candidate's other
 * applications (E5) — the copy says so explicitly.
 */

// snake/camel field name -> what a recruiter actually calls it. Only the fields
// the server can actually report: the score-affecting sets in
// match_retrigger/changed_fields.py. noticePeriod / expectedSalary are
// deliberately NOT score-affecting there, so they can never appear here —
// prettyField() covers anything added later.
const FIELD_LABELS = {
  summary: "Professional Summary",
  skills: "Skills & Language",
  workExperience: "Work Experience",
  education: "Education",
  additionalSections: "Additional Information",
  languages: "Languages",
  totalExperience: "Total Experience",
};

const prettyField = (f) =>
  FIELD_LABELS[f] ||
  String(f)
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();

const RetriggerConfirmModal = ({
  open,
  changedFields = [],
  submitting = false,
  // Which action is in flight, so the pending label lands on the button the
  // user actually pressed: "confirm" | "skip" | null. Falls back to "confirm"
  // when only `submitting` is passed.
  submittingAction = null,
  jobTitle,
  onDismiss,
  onSkip,
  onConfirm,
}) => (
  <Dialog
    open={open}
    onOpenChange={(nextOpen) => {
      // Closing (X, Esc, overlay click) == "never mind" — nothing is saved
      // and the user stays put with their edits. NOT the same as "Save as
      // it is", which commits.
      if (!nextOpen && !submitting) onDismiss?.();
    }}
  >
    <DialogContent className="w-[460px] md:w-full p-0">
      <DialogHeader className="relative px-5 py-3 border-b border-border-primary">
        <div className="pr-8">
          <DialogTitle className="text-[15px] font-semibold text-text-primary">Re-analyse this candidate?</DialogTitle>
          <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
            These edits affect how this candidate scores against {jobTitle ? `“${jobTitle}”` : "this job"}. Your changes haven’t been saved yet.
          </DialogDescription>
        </div>
        <DialogClose
          className="cursor-pointer absolute right-5 top-3 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center"
          disabled={submitting}
        >
          <X size={16} strokeWidth={3} />
        </DialogClose>
      </DialogHeader>

      <div className="px-5 py-4">
        <div className="text-[12px] font-medium text-text-primary mb-1.5">Changed fields</div>
        <ul className="flex flex-wrap gap-1.5 mb-3">
          {changedFields.map((field) => (
            <li key={field} className="text-[11px] px-2 py-0.5 rounded-full bg-background-icon/40 border border-border-primary text-text-primary">
              {prettyField(field)}
            </li>
          ))}
        </ul>

        <p className="text-[11px] text-text-primary/70 mb-2">
          Re-analysing spends an AI credit and only re-scores this application — it will not touch this candidate's other job applications.
        </p>

        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" className="border-none text-[11px]" type="button" disabled={submitting} onClick={() => onDismiss?.()}>
            Cancel
          </Button>

          <Button variant="outline" className="text-[11px]" type="button" disabled={submitting} onClick={onSkip}>
            {submitting && submittingAction === "skip" ? "Saving…" : "Save as it is"}
          </Button>

          <Button className="bg-button-secondary text-text-base text-[11px]" type="button" disabled={submitting} onClick={onConfirm}>
            {submitting && submittingAction !== "skip" ? "Saving…" : "Re-analyse and Save"}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default RetriggerConfirmModal;
